import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ModalSelecionarVenda from '../components/fiscal/ModalSelecionarVenda';
import ModalBuscaProdutos from '../components/pdv/ModalBuscaProdutos';
import useBuscaProdutosF3, { FILTROS_F3_VAZIOS } from '../hooks/useBuscaProdutosF3';
import { abrirDanfeNfePdf } from '../utils/impressaoVenda';
import s from './PedidosOrcamentos.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

const ABAS = [
  { id: 'dados',      label: 'Dados da NF-e' },
  { id: 'produtos',   label: 'Produtos' },
  { id: 'pagamento',  label: 'Formas Pagamento' },
  { id: 'cobranca',   label: 'Cobrança' },
  { id: 'transporte', label: 'Transporte' },
  { id: 'docref',     label: 'Documentos Referenciados' },
  { id: 'infoadic',   label: 'Informações Compl.' },
  { id: 'outros',     label: 'Outros' },
];
const ABAS_PRONTAS = new Set(['dados', 'produtos', 'pagamento', 'cobranca', 'transporte', 'docref', 'infoadic']);

const MODALIDADE_FRETE_OPTS = [
  ['0', '0 - Contratação do Frete por conta do Remetente (CIF)'],
  ['1', '1 - Contratação do Frete por conta do Destinatário (FOB)'],
  ['2', '2 - Contratação do Frete por conta de Terceiros'],
  ['3', '3 - Transporte Próprio por conta do Remetente'],
  ['4', '4 - Transporte Próprio por conta do Destinatário'],
  ['9', '9 - Sem Ocorrência de Transporte'],
].map(([value, label]) => ({ value, label }));

// Tipo do documento referenciado — só "nfe"/"nfce"/"cte" são transmitidos
// de verdade (referenciam por chave de acesso, 44 dígitos); os demais
// pedem outro formato (UF/AAMM/CNPJ/modelo/série/número) e ficam só
// cadastrados na tela por ora (ver nfe.js#NFrefXml).
const TIPO_DOC_REF_OPTS = [
  { value: 'nfe', label: 'NF-e' },
  { value: 'nfce', label: 'NFC-e' },
  { value: 'cte', label: 'CT-e' },
  { value: 'nota_fiscal', label: 'Nota Fiscal (modelo 1/1A)' },
  { value: 'nf_produtor', label: 'NF de Produtor' },
  { value: 'cupom_fiscal', label: 'Cupom Fiscal (ECF)' },
  { value: 'cfe', label: 'CF-e' },
];
const TIPO_DOC_REF_WIREADO = new Set(['nfe', 'nfce', 'cte']);

const FORMA_PGT_OPTS = [
  'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito',
  'PIX', 'Boleto', 'Transferência Bancária',
  'Cheque', 'Crediário', 'A Prazo',
  // tPag=90 (código oficial SEFAZ) — nota sem fins financeiros de verdade
  // (remessa pra conserto, brinde, amostra grátis etc.): a forma de
  // pagamento vai com valor zero, só a mercadoria/operação é declarada.
  'Sem Pagamento',
];

const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sv = v => (v == null ? '' : String(v));
let keySeq = 1;

// Opções dos combos da aba "Dados da NF-e" — códigos são os da própria
// SEFAZ (leiaute NF-e 4.00) quando o campo é transmitido de verdade; os
// marcados "(não transmitido ainda)" abaixo usam código interno só pra UI.
const DESTINO_OP_OPTS = [
  { value: '', label: '— Automático (pela UF do destinatário) —' },
  { value: '1', label: 'Interna' },
  { value: '2', label: 'Interestadual' },
  { value: '3', label: 'Exterior' },
];
const TIPO_DOC_OPTS = [
  { value: '1', label: 'Saída' },
  { value: '0', label: 'Entrada' },
];
const FINALIDADE_OPTS = [
  { value: '1', label: 'NF-e normal' },
  { value: '2', label: 'NF-e complementar' },
  { value: '3', label: 'NF-e de ajuste' },
  { value: '4', label: 'Devolução de mercadoria' },
];
// Situação do documento — campo do sistema de referência sem tag equivalente
// na transmissão (o status real do documento é controlado pela própria
// SEFAZ/pelo cancelamento, não escolhido na hora de criar a nota); mantido
// só pra paridade visual, não transmitido ainda.
const SITUACAO_DOC_OPTS = [
  { value: 'regular', label: 'Documento regular' },
  { value: 'extemp_regular', label: 'Escrituração extemporânea de documento regular' },
  { value: 'cancelado', label: 'Documento cancelado' },
  { value: 'extemp_cancelado', label: 'Escrituração extemporânea de documento cancelado' },
  { value: 'denegado', label: 'NF-e, NFC-e ou CT-e denegado' },
  { value: 'complementar', label: 'Documento Fiscal Complementar' },
  { value: 'extemp_complementar', label: 'Escrituração extemporânea de documento complementar' },
  { value: 'regime_especial', label: 'Documento Fiscal emitido com base em Regime Especial ou Norma Específica' },
];
const INDICADOR_PRESENCA_OPTS = [
  { value: '0', label: 'Não se aplica' },
  { value: '1', label: 'Operação presencial' },
  { value: '2', label: 'Operação não presencial, pela Internet' },
  { value: '3', label: 'Operação não presencial, Tele atendimento' },
  { value: '4', label: 'NFC-e em operação com entrega a domicílio' },
  { value: '5', label: 'Operação presencial, fora do estabelecimento' },
  { value: '9', label: 'Operação não presencial, outros' },
];
const INDICADOR_FINALIZACAO_OPTS = [
  { value: '0', label: 'Normal' },
  { value: '1', label: 'Consumidor Final' },
];
// Forma de pagamento (indicador único de cabeçalho) — não transmitida: o
// detalhamento real por forma de pagamento já é a aba "Formas Pagamento".
const FORMA_PGT_HEADER_OPTS = [
  { value: 'vista', label: 'Pagamento a Vista' },
  { value: 'prazo', label: 'Pagamento a Prazo' },
  { value: 'outros', label: 'Outros' },
];
// Aplicação material — não transmitida (sem tag equivalente no leiaute).
const APLICACAO_MATERIAL_OPTS = ['REVENDA', 'IMOBILIZADO', 'ISENTO'];
const INDICADOR_INTERMEDIADOR_OPTS = [
  { value: '0', label: 'Operação sem intermediador' },
  { value: '1', label: 'Operação em site ou plataformas de terceiros' },
];

const agora = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const FORM_VAZIO = {
  venda_id: null, venda_codigo: '', venda_data: '',
  origem: null, // 'venda' | 'avulso' — os dois emitem de verdade agora
  documento_id: null, // linha em documentos_fiscais (rascunho ou já emitida) — ver salvarRascunhoNfe
  situacao: null, // status real de documentos_fiscais quando carregado via Buscar (rascunho/autorizada/...)
  doc_fiscal_tipo: null, doc_fiscal_status: null, doc_fiscal_numero: null, doc_fiscal_serie: null,
  cliente_id: null, cliente_nome: '', cliente_cpf_cnpj: '',
  tipo_movimento_fiscal_id: '', tipo_movimento_nome: '', natureza_operacao: '',
  itens: [], pagamentos: [],
  // ── Aba "Dados da NF-e" — defaults conforme combinado, editáveis ──
  endereco_entrega: null, // { logradouro, numero, bairro, cidade, uf, cep } | null
  saida_entrada_marcado: true,
  saida_entrada: agora(),
  destino_operacao: '',            // '' = automático (recomendado)
  tipo_doc: '1',                   // Saída
  finalidade_emissao: '1',         // NF-e normal
  situacao_documento: 'regular',   // não transmitido — só UI
  indicador_presenca: '9',         // Não presencial, outros
  indicador_finalizacao: '0',      // Normal
  forma_pagamento_cab: 'vista',    // não transmitido — só UI
  aplicacao_material: 'REVENDA',   // não transmitido — só UI
  ie_st: '',                       // não transmitido — só UI
  indicador_intermediador: '0',    // Sem intermediador
  cnpj_intermediador: '',          // não transmitido ainda
  usuario_intermediador: '',       // não transmitido ainda
  receituario_agricola: '',        // não transmitido — só UI
  cpf_responsavel_tecnico: '',     // não transmitido — só UI
  // ── Aba "Cobrança" — <cobr> (fatura + duplicatas) ──
  cobranca: {
    numero_fatura: '', valor_original: '', valor_desconto: '', valor_liquido: '',
    duplicatas: [], // { _key, numero, vencimento, valor }
  },
  // ── Aba "Transporte" — <transp> (modalidade + transportadora + veículo + volumes) ──
  transporte: {
    modalidade_frete: '9',
    transportadora_nome: '', transportadora_cpf_cnpj: '', transportadora_ie: '',
    transportadora_endereco: '', transportadora_municipio: '', transportadora_uf: '',
    veiculo_habilitado: false, veiculo_placa: '', veiculo_uf: '', veiculo_rntrc: '',
    volumes: [], // { _key, qtd, especie, marca, numeracao, peso_liquido, peso_bruto }
  },
  // ── Aba "Documentos Referenciados" — <NFref> (só nfe/nfce/cte transmitidos) ──
  documentos_referenciados: [], // { _key, tipo, chave_acesso }
  // ── Aba "Informações Compl." — <infAdic><infCpl> ──
  informacoes_complementares: '',
};

// ── Modal simples de busca de cliente (troca do destinatário) ──
function ModalBuscaClienteSimples({ onSelect, onClose }) {
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState([]);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (busca.length < 2) { setLista([]); return; }
    api.get('/clientes', { params: { busca, todos: 'true' } }).then(r => setLista(r.data)).catch(() => {});
  }, [busca]);
  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <div className={s.modalTitle}>👤 Buscar Cliente</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input ref={inputRef} className={s.inputErp} style={{ flex: 1 }}
              placeholder="Nome, CPF/CNPJ, código..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && onClose()} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className={s.modalTabela}>
              <thead><tr><th>Cód.</th><th>Nome</th><th>CPF/CNPJ</th><th>Cidade</th></tr></thead>
              <tbody>
                {lista.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 10, color: '#888', textAlign: 'center' }}>
                    {busca.length < 2 ? 'Digite para buscar...' : 'Nenhum resultado.'}
                  </td></tr>
                )}
                {lista.map(c => (
                  <tr key={c.id} onClick={() => onSelect(c)}>
                    <td>{c.codigo}</td><td>{c.nome}</td>
                    <td>{c.cpf_cnpj || '—'}</td><td>{c.cidade || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Campos da aba "Dados da NF-e" — label em cima, valor embaixo, com ⓘ
// opcional pros campos que ainda não são transmitidos (só cadastro) ──
const campoWrap = { display: 'flex', flexDirection: 'column', gap: 3 };
const campoLbl  = { fontSize: 11, color: '#555', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 };
const infoIcone = info => info && <span title={info} style={{ cursor: 'help', color: '#b45309' }}>ⓘ</span>;

function CampoTexto({ label, value, onChange, disabled, readOnly, placeholder, info, maxLength }) {
  return (
    <div style={campoWrap}>
      <span style={campoLbl}>{label} {infoIcone(info)}</span>
      <input className={s.inputErp} style={{ width: '100%', boxSizing: 'border-box' }}
        value={value || ''} disabled={disabled} readOnly={readOnly} maxLength={maxLength}
        placeholder={placeholder} onChange={e => onChange?.(e.target.value)} />
    </div>
  );
}

function CampoSelect({ label, value, onChange, opts, disabled, info }) {
  return (
    <div style={campoWrap}>
      <span style={campoLbl}>{label} {infoIcone(info)}</span>
      <select className={s.selectErp} style={{ width: '100%', boxSizing: 'border-box' }}
        value={value} disabled={disabled} onChange={e => onChange?.(e.target.value)}>
        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ── Modal "Endereço de entrega" (<entrega> — só emitido quando informado) ──
function ModalEnderecoEntrega({ inicial, onSalvar, onClose }) {
  const [end, setEnd] = useState(inicial || { logradouro: '', numero: '', bairro: '', cidade: '', uf: '', cep: '' });
  const campo = k => e => setEnd(prev => ({ ...prev, [k]: e.target.value }));
  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ maxWidth: 440 }}>
        <div className={s.modalTitle}>📍 Endereço de entrega</div>
        <div className={s.modalBody} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14 }}>
          <input className={s.inputErp} placeholder="Logradouro" value={end.logradouro} onChange={campo('logradouro')} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input className={s.inputErp} style={{ width: 100 }} placeholder="Número" value={end.numero} onChange={campo('numero')} />
            <input className={s.inputErp} style={{ flex: 1 }} placeholder="Bairro" value={end.bairro} onChange={campo('bairro')} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className={s.inputErp} style={{ flex: 1 }} placeholder="Cidade" value={end.cidade} onChange={campo('cidade')} />
            <input className={s.inputErp} style={{ width: 56 }} placeholder="UF" maxLength={2}
              value={end.uf} onChange={e => setEnd(prev => ({ ...prev, uf: e.target.value.toUpperCase() }))} />
            <input className={s.inputErp} style={{ width: 120 }} placeholder="CEP" value={end.cep} onChange={campo('cep')} />
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
          <button className={s.btnErpPrimario} onClick={() => onSalvar(end)} disabled={!end.logradouro?.trim()}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

// ── Opções de tributos (códigos oficiais da SEFAZ) ──
const ORIGEM_OPTS = [
  ['0', '0 - Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8'],
  ['1', '1 - Estrangeira - Importação direta'],
  ['2', '2 - Estrangeira - Adquirida no mercado interno'],
  ['3', '3 - Nacional - Conteúdo de importação > 40%'],
  ['4', '4 - Nacional - Processos produtivos básicos'],
  ['5', '5 - Nacional - Conteúdo de importação ≤ 40%'],
  ['6', '6 - Estrangeira - Importação direta, sem similar nacional'],
  ['7', '7 - Estrangeira - Mercado interno, sem similar nacional'],
  ['8', '8 - Nacional - Conteúdo de importação > 70%'],
].map(([value, label]) => ({ value, label }));
const CST_ICMS_OPTS = [
  ['00', '00 - Tributada integralmente'],
  ['10', '10 - Tributada e com cobrança do ICMS por ST'],
  ['20', '20 - Com redução de base de cálculo'],
  ['30', '30 - Isenta/não tributada e com cobrança do ICMS por ST'],
  ['40', '40 - Isenta'],
  ['41', '41 - Não tributada'],
  ['50', '50 - Suspensão'],
  ['51', '51 - Diferimento'],
  ['60', '60 - ICMS cobrado anteriormente por ST'],
  ['70', '70 - Com redução de BC e cobrança do ICMS por ST'],
  ['90', '90 - Outras'],
].map(([value, label]) => ({ value, label }));
const CSOSN_OPTS = [
  ['101', '101 - Tributada c/ permissão de crédito'],
  ['102', '102 - Tributada sem permissão de crédito'],
  ['103', '103 - Isenção (faixa de receita bruta)'],
  ['201', '201 - Trib. c/ crédito e cobrança do ICMS por ST'],
  ['202', '202 - Trib. sem crédito e cobrança do ICMS por ST'],
  ['203', '203 - Isenção e cobrança do ICMS por ST'],
  ['300', '300 - Imune'],
  ['400', '400 - Não tributada pelo Simples Nacional'],
  ['500', '500 - ICMS cobrado anteriormente por ST/antecipação'],
  ['900', '900 - Outros'],
].map(([value, label]) => ({ value, label }));
const MOD_BC_OPTS = [
  ['0', '0 - Margem Valor Agregado (%)'], ['1', '1 - Pauta (valor)'],
  ['2', '2 - Preço Tabelado Máximo (valor)'], ['3', '3 - Valor da operação'],
].map(([value, label]) => ({ value, label }));
const MOD_BC_ST_OPTS = [
  ['0', '0 - Preço tabelado ou máximo sugerido'], ['1', '1 - Lista Negativa (valor)'],
  ['2', '2 - Lista Positiva (valor)'], ['3', '3 - Lista Neutra (valor)'],
  ['4', '4 - Margem Valor Agregado (%)'], ['5', '5 - Pauta (valor)'], ['6', '6 - Valor da operação'],
].map(([value, label]) => ({ value, label }));

// ── Modal "Tributos do item" — CST/CSOSN, ICMS próprio, ICMS-ST, crédito de
// ICMS do Simples Nacional. Cada campo cai no valor do cadastro (produto_
// fiscal_saida) quando fica vazio — não sobrescreve nada se o usuário só
// olhar e fechar (ver fiscalController.emitirNfe#itensOverride). ──
function ModalItemTributos({ item, regimeNormal, onSalvar, onClose }) {
  const [v, setV] = useState(item);
  const campo = k => val => setV(prev => ({ ...prev, [k]: val }));
  // CST/CSOSN é UM campo na tela — grava nos dois (csosn e cst_icms) pra
  // valer independente do regime (o backend lê um ou outro conforme o CRT).
  const cstCsosn = regimeNormal ? v.cst_icms : v.csosn;
  const setCstCsosn = val => setV(prev => ({ ...prev, cst_icms: val, csosn: val }));
  const ehCsosn101 = !regimeNormal && cstCsosn === '101';

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ maxWidth: 720 }}>
        <div className={s.modalTitle}>🧮 Tributos do item — {item.descricao}</div>
        <div className={s.modalBody} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px 14px' }}>
            <CampoSelect label="Origem" value={v.origem_fiscal || '0'} onChange={campo('origem_fiscal')} opts={ORIGEM_OPTS} />
            <CampoSelect label={regimeNormal ? 'CST' : 'CSOSN'} value={cstCsosn || ''} onChange={setCstCsosn}
              opts={regimeNormal ? CST_ICMS_OPTS : CSOSN_OPTS} />
          </div>

          {regimeNormal && (
            <>
              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#444', borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                ICMS próprio
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 14px' }}>
                <CampoSelect label="Modalidade BC" value={v.icms_mod_bc || '3'} onChange={campo('icms_mod_bc')} opts={MOD_BC_OPTS} />
                <CampoTexto label="Redução BC %" value={v.icms_red_bc_perc} onChange={campo('icms_red_bc_perc')} />
                <CampoTexto label="Alíquota %" value={v.icms_aliq_perc} onChange={campo('icms_aliq_perc')} />
                <CampoTexto label="Diferimento %" value={v.icms_dif_perc} onChange={campo('icms_dif_perc')}
                  info="Só usado no CST 51 (Diferimento)." />
              </div>

              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#444', borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                ICMS Substituição Tributária
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 14px' }}>
                <CampoSelect label="Modalidade BC ST" value={v.icms_st_mod_bc || '4'} onChange={campo('icms_st_mod_bc')} opts={MOD_BC_ST_OPTS} />
                <CampoTexto label="Redução BC ST %" value={v.icms_st_red_bc_perc} onChange={campo('icms_st_red_bc_perc')} />
                <CampoTexto label="MVA %" value={v.icms_st_mva_perc} onChange={campo('icms_st_mva_perc')} />
                <CampoTexto label="Alíquota ST %" value={v.icms_st_aliq_perc} onChange={campo('icms_st_aliq_perc')} />
                <CampoTexto label="BC ST retido R$" value={v.icms_st_ret_bc} onChange={campo('icms_st_ret_bc')}
                  info="Só usado no CST 60 (ICMS cobrado antes por ST)." />
                <CampoTexto label="ICMS ST retido R$" value={v.icms_st_ret_valor} onChange={campo('icms_st_ret_valor')}
                  info="Só usado no CST 60 (ICMS cobrado antes por ST)." />
              </div>
            </>
          )}

          {!regimeNormal && (
            <>
              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#444', borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                Crédito de ICMS do Simples Nacional
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 14px' }}>
                <CampoTexto label="Alíquota de cálculo do crédito %" value={v.pCredSN} onChange={campo('pCredSN')}
                  disabled={!ehCsosn101} info="Só se aplica ao CSOSN 101." />
                <CampoTexto label="Crédito que pode ser aproveitado R$" value={v.vCredICMSSN} onChange={campo('vCredICMSSN')}
                  disabled={!ehCsosn101} info="Só se aplica ao CSOSN 101." />
              </div>
            </>
          )}

          <p style={{ fontSize: 11, color: '#888', margin: 0 }}>
            Campo vazio = usa o cadastrado no produto (aba Fiscal → Saída). FCP e IPI por item ainda não
            são suportados nesta tela — recursos maiores, ficam pra uma próxima rodada.
          </p>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
          <button className={s.btnErpPrimario} onClick={() => onSalvar(v)}>Ok</button>
        </div>
      </div>
    </div>
  );
}

const SITUACAO_BUSCA_OPTS = [
  ['rascunho', 'Em digitação'], ['autorizada', 'Autorizada'],
  ['rejeitada', 'Rejeitada'], ['cancelada', 'Cancelada'], ['epec', 'EPEC (contingência)'],
];

// ── Modal "Buscar NF-e" — rascunhos + já emitidas, filtro por período/
// situação/número/destinatário. Botão Buscar do rodapé. ──
function ModalBuscarNfe({ onBuscar, resultados, onSelecionar, onClose }) {
  const [dataDe, setDataDe]         = useState('');
  const [dataAte, setDataAte]       = useState('');
  const [situacoes, setSituacoes]   = useState(new Set());
  const [numero, setNumero]         = useState('');
  const [destinatario, setDestinatario] = useState('');
  const [buscando, setBuscando]     = useState(false);
  const [sel, setSel]               = useState(null);

  const toggleSituacao = (v) => setSituacoes(prev => {
    const novo = new Set(prev);
    novo.has(v) ? novo.delete(v) : novo.add(v);
    return novo;
  });

  const buscar = async () => {
    setBuscando(true);
    try {
      await onBuscar({
        data_de: dataDe || undefined, data_ate: dataAte || undefined,
        status: situacoes.size ? Array.from(situacoes).join(',') : undefined,
        numero: numero || undefined, destinatario: destinatario || undefined,
      });
    } finally { setBuscando(false); }
  };

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ maxWidth: 900 }}>
        <div className={s.modalTitle}>🔍 Buscar NF-e</div>
        <div className={s.modalBody}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              Emissão de
              <InputDataFiltro className={s.inputErp} value={dataDe} onChange={e => setDataDe(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              até
              <InputDataFiltro className={s.inputErp} value={dataAte} onChange={e => setDataAte(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              Número
              <input className={s.inputErp} style={{ width: 100 }} value={numero} onChange={e => setNumero(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, flex: 1, minWidth: 160 }}>
              Destinatário
              <input className={s.inputErp} value={destinatario} onChange={e => setDestinatario(e.target.value)} />
            </label>
            <button className={s.btnErpPrimario} onClick={buscar} disabled={buscando}>
              {buscando ? 'Buscando...' : 'Filtrar'}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10, fontSize: 12 }}>
            {SITUACAO_BUSCA_OPTS.map(([v, label]) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={situacoes.has(v)} onChange={() => toggleSituacao(v)} /> {label}
              </label>
            ))}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className={s.modalTabela}>
              <thead>
                <tr><th>Número</th><th>Destinatário</th><th>Emissão</th><th>Situação</th><th style={{ textAlign: 'right' }}>Total</th></tr>
              </thead>
              <tbody>
                {resultados.length === 0 && !buscando && (
                  <tr><td colSpan={5} style={{ padding: 10, color: '#888', textAlign: 'center' }}>Nenhum resultado.</td></tr>
                )}
                {resultados.map((d, idx) => (
                  <tr key={d.id} className={sel === idx ? s.selRow : ''}
                    onClick={() => setSel(idx)} onDoubleClick={() => onSelecionar(d, idx)}>
                    <td style={{ fontWeight: 'bold' }}>{d.numero || '(rascunho)'}{d.serie ? `/${d.serie}` : ''}</td>
                    <td>{d.cliente_nome || '—'}</td>
                    <td>{d.criado_em ? new Date(d.criado_em).toLocaleString('pt-BR') : '—'}</td>
                    <td>{SITUACAO_BUSCA_OPTS.find(([v]) => v === d.status)?.[1] || d.status}</td>
                    <td style={{ textAlign: 'right' }}>{d.valor_total != null ? fmtBRL(d.valor_total) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => sel != null && onSelecionar(resultados[sel], sel)} disabled={sel == null}>Ok</button>
          <button className={s.btnErp} onClick={onClose}>Sair</button>
        </div>
      </div>
    </div>
  );
}

export default function NotaFiscalManual() {
  const { usuario } = useAuth();
  const [form, setForm]           = useState(FORM_VAZIO);
  const [aba, setAba]              = useState('dados');
  const [tiposMovimento, setTiposMovimento] = useState([]);
  // Decide se o item usa CST (regime normal) ou CSOSN (Simples Nacional) —
  // mesmo critério de crtDoRegime (services/sefaz/icms.js): só "Lucro..." é
  // regime normal, todo o resto (inclusive sem regime cadastrado) é SN.
  const [regimeNormal, setRegimeNormal] = useState(false);
  const [modalNovo, setModalNovo]         = useState(false);
  const [modalSelecionarVenda, setModalSelecionarVenda] = useState(false);
  const [modalCliente, setModalCliente]   = useState(false);
  const [modalEntrega, setModalEntrega]   = useState(false);
  const [modalItem, setModalItem]         = useState(null); // _key do item em edição de tributos
  const [carregandoVenda, setCarregandoVenda] = useState(false);
  const [emitindo, setEmitindo]     = useState(false);
  const [resultado, setResultado]   = useState(null);
  const [erro, setErro]             = useState('');
  // "Alterar" — registro carregado via Buscar entra travado (modoEdicao=
  // false); Novo/Avulso/De uma Venda já entram destravados. O fieldset da
  // área de abas usa isso pra desabilitar TODOS os campos de uma vez, sem
  // precisar passar disabled campo a campo.
  const [modoEdicao, setModoEdicao] = useState(true);
  const [modalBuscar, setModalBuscar]     = useState(false);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [indiceBusca, setIndiceBusca]     = useState(null); // posição atual em resultadosBusca
  const [salvando, setSalvando]           = useState(false);
  const [excluindo, setExcluindo]         = useState(false);
  // Totais do rodapé (ICMS/PIS/COFINS/produtos/desconto/total) — vêm do
  // preview ao vivo (POST /fiscal/nfe/preview-totais), nunca de uma conta
  // feita na tela: são exatamente os que iriam pro XML se "Emitir" fosse
  // clicado agora (ver fiscalController.previewTotaisNfe).
  const [totais, setTotais]               = useState(null);
  const [abrindoDanfe, setAbrindoDanfe]   = useState(false);
  // Endereço de entrega: por padrão mostra o do CADASTRO do destinatário
  // (pedido explícito) — só usa form.endereco_entrega quando o usuário
  // clicou "Informar" e digitou um diferente.
  const [clienteEndereco, setClienteEndereco] = useState(null);
  const focoQtdRef = useRef({});   // _key -> <input> de Quantidade, pro lançamento guiado
  const focoPrecoRef = useRef({}); // _key -> <input> de Valor Unit.

  // Busca de produto (F3) — mesmo componente/hook compartilhado do PDV e
  // Pedidos/Orçamentos, só pra "2. Avulso" (nota sem venda de origem): lá
  // não tem venda pra puxar os itens, então o usuário lança um a um.
  // Precisa vir cedo (antes do useEffect de teclado logo abaixo, que usa
  // modalF3/idxF3/confirmarF3) — senão dá erro de "usado antes de declarado".
  const {
    modalF3, setFiltro, filtrosF3, setFiltrosF3, produtosF3, setProdutosF3,
    buscandoF3, idxF3, setIdxF3, buscaRapidaF3, setBuscaRapidaF3, buscaRapidaRef,
    fecharF3, buscarF3, handleBuscaRapidaF3, abrirF3,
  } = useBuscaProdutosF3({});

  // Dados cadastrais/tributários vêm do cadastro do produto NA HORA de
  // adicionar (GET /produtos/:id — só esse endpoint traz produto_fiscal_
  // saida; a lista do F3 é enxuta demais) — servem só de PONTO DE PARTIDA
  // editável. A edição fica só no item lançado nesta nota; nunca grava de
  // volta no cadastro do produto (pedido explícito).
  const confirmarF3 = async (produto) => {
    fecharF3();
    try {
      const { data: p } = await api.get(`/produtos/${produto.id}`);
      const fs = p.fiscal_saida || {};
      const key = keySeq++;
      setForm(f => ({
        ...f,
        itens: [...f.itens, {
          _key: key,
          produto_id: p.id,
          codigo_interno: p.codigo_interno || '',
          descricao: p.nome || '',
          unidade: p.unidade || 'UN',
          quantidade: '1',
          preco_unitario: sv(p.preco_venda),
          subtotal: sv(p.preco_venda),
          cfop: '',
          // Só pra MOSTRAR o CFOP do cadastro no placeholder da grade (troca
          // o texto genérico "auto") — nunca é enviado na emissão; o CFOP de
          // verdade continua resolvido pelo Tipo de Movimento (ver
          // fiscalController.aplicarTipoMovimento), sobrescrito por este
          // campo só quando o usuário digita algo nele.
          cfop_saida: p.cfop_saida || '',
          desconto_perc: '',
          ncm: p.ncm || '', cest: p.cest || '', origem_fiscal: sv(p.origem_fiscal ?? '0'),
          csosn: sv(fs.icms_cst ?? p.csosn), cst_icms: sv(fs.icms_cst ?? p.cst_icms),
          icms_mod_bc: sv(fs.icms_mod_bc), icms_red_bc_perc: sv(fs.icms_red_bc_perc),
          icms_aliq_perc: sv(fs.icms_aliq_perc), icms_dif_perc: sv(fs.icms_dif_perc),
          icms_st_mod_bc: sv(fs.icms_st_mod_bc), icms_st_red_bc_perc: sv(fs.icms_st_red_bc_perc),
          icms_st_mva_perc: sv(fs.icms_st_mva_perc), icms_st_aliq_perc: sv(fs.icms_st_aliq_perc),
          icms_st_ret_bc: sv(fs.icms_st_ret_bc), icms_st_ret_valor: sv(fs.icms_st_ret_valor),
          pCredSN: '', vCredICMSSN: '',
        }],
      }));
      // Lançamento guiado (igual Pedidos/Orçamentos): foca Quantidade assim
      // que o item entra na grade — Enter lá vai pro Valor Unit., Enter lá
      // salva e já reabre o F3 pro próximo produto (ver onKeyDown das duas
      // colunas, aba Produtos).
      setTimeout(() => focoQtdRef.current[key]?.select(), 60);
    } catch {
      setErro('Erro ao carregar os dados fiscais do produto.');
    }
  };

  const carregado = !!form.venda_id || form.origem === 'avulso';
  const jaEmitido = form.doc_fiscal_status === 'autorizada' || form.doc_fiscal_status === 'epec';

  useEffect(() => {
    api.get('/fiscal/tipos-movimento').then(r => setTiposMovimento(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!usuario?.organizacao_id) return;
    api.get(`/organizacoes/${usuario.organizacao_id}`)
      .then(({ data }) => setRegimeNormal(String(data.regime_tributario || '').toLowerCase().includes('lucro')))
      .catch(() => {});
  }, [usuario]);

  // Endereço de entrega — mostra o do CADASTRO do destinatário por padrão
  // (pedido explícito); só busca de novo quando o cliente muda.
  useEffect(() => {
    if (!form.cliente_id) { setClienteEndereco(null); return; }
    api.get(`/clientes/${form.cliente_id}`)
      .then(({ data }) => setClienteEndereco(data))
      .catch(() => setClienteEndereco(null));
  }, [form.cliente_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // "PRÓXIMO..." — avança pra próxima aba na ordem de ABAS (mesma sequência
  // do menu). Funciona mesmo nas abas "em breve" (só some na aba, o clique
  // não quebra nada — fica pronto pra quando elas ganharem conteúdo).
  const proximaAba = () => {
    const idx = ABAS.findIndex(a => a.id === aba);
    if (idx >= 0 && idx < ABAS.length - 1) setAba(ABAS[idx + 1].id);
  };

  // Rascunho — salva automaticamente (debounced) a cada mudança, pra "sair
  // e voltar continuar de onde parou" funcionar de verdade — vale pros dois
  // fluxos agora (De uma Venda também ganha rascunho, não só Avulso).
  // Reaproveita a MESMA linha em documentos_fiscais (form.documento_id) o
  // tempo todo — não cria uma nova a cada save (ver fiscalController.
  // salvarRascunhoNfe). Só roda em modo de edição — um registro carregado
  // via Buscar em modo leitura não deve ser sobrescrito sozinho.
  const rascunhoSalvarRef = useRef(null);
  useEffect(() => {
    if (!form.documento_id || !modoEdicao) return;
    clearTimeout(rascunhoSalvarRef.current);
    rascunhoSalvarRef.current = setTimeout(() => {
      api.post('/fiscal/nfe/rascunho', { id: form.documento_id, venda_id: form.venda_id || undefined, dados: form }).catch(() => {});
    }, 800);
    return () => clearTimeout(rascunhoSalvarRef.current);
  }, [form, modoEdicao]);

  // Totais do rodapé — mesmo debounce/gatilho do autosave de rascunho, mas
  // roda também em modo leitura (registro carregado via Buscar continua
  // mostrando os totais de verdade). Precisa de destinatário + item — sem
  // isso o preview nem chegaria em validarClienteNfe/validarDadosFiscais no
  // backend, então nem tenta (evita um 422 previsível a cada tecla).
  const totaisPreviewRef = useRef(null);
  useEffect(() => {
    if (!carregado || !form.itens.length || !form.cliente_id) { setTotais(null); return; }
    clearTimeout(totaisPreviewRef.current);
    totaisPreviewRef.current = setTimeout(() => {
      api.post('/fiscal/nfe/preview-totais', montarCorpoEmissao(form))
        .then(({ data }) => setTotais(data.totais))
        .catch(() => setTotais(null));
    }, 800);
    return () => clearTimeout(totaisPreviewRef.current);
  }, [form, carregado]);

  /* ── Novo (F4) ── */
  const abrirNovo = useCallback(() => { setErro(''); setResultado(null); setModalNovo(true); }, []);

  useEffect(() => {
    const h = e => {
      // Modal F3 (busca de produto, "+ Adicionar Produto") — mesmo padrão
      // de teclado do PDV/Pedidos (setas navegam a lista, Enter confirma a
      // linha selecionada); achado ausente aqui (2026-09-15) — o modal em
      // si não trata teclado nenhum, cada tela precisa ligar isso por fora.
      if (modalF3) {
        if (e.key === 'Escape') { fecharF3(); return; }
        if (e.key === 'Enter' && idxF3 !== null) { e.preventDefault(); confirmarF3(produtosF3[idxF3]); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.min(i + 1, produtosF3.length - 1)); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.max(i - 1, 0)); return; }
        return;
      }
      if (e.key === 'F4' && !modalNovo && !modalSelecionarVenda && !modalCliente) {
        e.preventDefault(); abrirNovo();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [modalNovo, modalSelecionarVenda, modalCliente, abrirNovo, modalF3, idxF3, produtosF3, fecharF3, confirmarF3, setIdxF3]);

  const escolherOrigemVenda = () => {
    setModalNovo(false);
    setModalSelecionarVenda(true);
  };

  // "2. Avulso" — abre um rascunho em branco pra preencher a aba "Dados da
  // NF-e" do zero. A emissão de verdade sem venda de origem ainda não está
  // implementada no backend (itens/destinatário 100% manuais é trabalho
  // maior, fica pra quando as abas Produtos/Formas Pagamento forem tratadas
  // pra esse fluxo) — por ora dá pra navegar e preencher os campos, mas o
  // botão "Emitir" fica desabilitado com aviso.
  const iniciarAvulso = async () => {
    setModalNovo(false);
    setErro(''); setResultado(null);
    setForm({ ...FORM_VAZIO, origem: 'avulso', situacao: 'rascunho' });
    setModoEdicao(true);
    setAba('dados');
    // Cria o rascunho já de cara (documentos_fiscais, status='rascunho',
    // sem número) — é essa linha que vai sendo salva a cada mudança e que
    // "vira" a nota de verdade quando o usuário emitir.
    try {
      const { data } = await api.post('/fiscal/nfe/rascunho', { dados: {} });
      setForm(f => ({ ...f, documento_id: data.id }));
    } catch {
      setErro('Erro ao iniciar o rascunho — tente novamente.');
    }
  };

  const selecionarVenda = async (vendaResumo) => {
    setModalSelecionarVenda(false);
    setCarregandoVenda(true);
    setErro('');
    try {
      const { data: v } = await api.get(`/vendas/${vendaResumo.id}`);
      const tmf = tiposMovimento.find(t => t.id === v.tipo_movimento_fiscal_id);
      const novoForm = {
        ...FORM_VAZIO,
        venda_id: v.id,
        venda_codigo: sv(v.id),
        venda_data: v.criado_em,
        origem: 'venda', situacao: 'rascunho',
        // Já tem NF-e/NFC-e autorizada ou EPEC pendente? Bloqueia reemissão
        // (backend também recusa — ver fiscalController.bloquearSeJaEmitido —
        // isso aqui só evita a viagem ao servidor pra descobrir).
        doc_fiscal_tipo:   v.doc_fiscal_tipo || null,
        doc_fiscal_status: v.doc_fiscal_status || null,
        doc_fiscal_numero: v.doc_fiscal_numero || null,
        doc_fiscal_serie:  v.doc_fiscal_serie || null,
        cliente_id: v.cliente_id || null,
        cliente_nome: v.cliente_nome || 'VENDA CONSUMIDOR',
        cliente_cpf_cnpj: v.cpf_cnpj || '',
        tipo_movimento_fiscal_id: v.tipo_movimento_fiscal_id ? sv(v.tipo_movimento_fiscal_id) : '',
        tipo_movimento_nome: tmf?.nome || '',
        natureza_operacao: tmf?.natureza_operacao || '',
        itens: (v.itens || [])
          .filter(i => (i.tipo_item || i.tipo || 'mercadoria').toLowerCase() !== 'serviço')
          .map(i => ({
            _key: keySeq++,
            produto_id: i.produto_id || null,
            codigo_interno: i.produto_codigo_interno || '',
            descricao: i.descricao || i.produto_nome || '',
            unidade: i.unidade || 'UN',
            quantidade: sv(i.quantidade),
            preco_unitario: sv(i.preco_unitario),
            subtotal: sv(i.subtotal),
            cfop: '',
            // Só pra MOSTRAR no placeholder da grade — ver confirmarF3 acima.
            cfop_saida: i.cfop_saida || '',
            desconto_perc: '',
            // Tributos — vêm do cadastro (produto_fiscal_saida, já resolvido
            // pelo backend em GET /vendas/:id), editáveis no modal por item.
            ncm: i.ncm || '', cest: i.cest || '', origem_fiscal: sv(i.origem_fiscal ?? '0'),
            csosn: sv(i.csosn), cst_icms: sv(i.cst_icms),
            icms_mod_bc: sv(i.icms_mod_bc), icms_red_bc_perc: sv(i.icms_red_bc_perc),
            icms_aliq_perc: sv(i.icms_aliq_perc), icms_dif_perc: sv(i.icms_dif_perc),
            icms_st_mod_bc: sv(i.icms_st_mod_bc), icms_st_red_bc_perc: sv(i.icms_st_red_bc_perc),
            icms_st_mva_perc: sv(i.icms_st_mva_perc), icms_st_aliq_perc: sv(i.icms_st_aliq_perc),
            icms_st_ret_bc: sv(i.icms_st_ret_bc), icms_st_ret_valor: sv(i.icms_st_ret_valor),
            pCredSN: '', vCredICMSSN: '',
          })),
        pagamentos: (v.pagamentos || []).map(p => ({
          _key: keySeq++, forma_pagamento: p.forma_pagamento, valor: sv(p.valor),
        })),
      };
      setForm(novoForm);
      setModoEdicao(true);
      setAba('dados');
      // Rascunho já nasce aqui também (não só no avulso) — é o que faz
      // Salvar/Buscar/navegação funcionarem igual nos dois fluxos.
      try {
        const { data } = await api.post('/fiscal/nfe/rascunho', { venda_id: v.id, dados: novoForm });
        setForm(f => ({ ...f, documento_id: data.id }));
      } catch { /* segue sem rascunho — autosave tenta de novo na próxima edição */ }
    } catch {
      setErro('Erro ao carregar a venda selecionada.');
    } finally {
      setCarregandoVenda(false);
    }
  };

  const escolherTipoMovimento = (id) => {
    const tmf = tiposMovimento.find(t => sv(t.id) === id);
    setForm(f => ({
      ...f,
      tipo_movimento_fiscal_id: id,
      tipo_movimento_nome: tmf?.nome || '',
      natureza_operacao: tmf?.natureza_operacao || '',
    }));
  };

  const selecionarCliente = (c) => {
    setModalCliente(false);
    setForm(f => ({ ...f, cliente_id: c.id, cliente_nome: c.nome, cliente_cpf_cnpj: c.cpf_cnpj || '' }));
  };

  /* ── Itens ── */
  const setItem = (key, campo, valor) => {
    setForm(f => ({
      ...f,
      itens: f.itens.map(it => {
        if (it._key !== key) return it;
        const novo = { ...it, [campo]: valor };
        if (campo === 'quantidade' || campo === 'preco_unitario') {
          const qtd = parseFloat(campo === 'quantidade' ? valor : novo.quantidade) || 0;
          const preco = parseFloat(campo === 'preco_unitario' ? valor : novo.preco_unitario) || 0;
          novo.subtotal = (qtd * preco).toFixed(2);
        }
        return novo;
      }),
    }));
  };
  const removerItem = (key) => setForm(f => ({ ...f, itens: f.itens.filter(it => it._key !== key) }));
  const salvarTributosItem = (novo) => {
    setForm(f => ({ ...f, itens: f.itens.map(it => it._key === novo._key ? novo : it) }));
    setModalItem(null);
  };
  const totalItens = form.itens.reduce((s2, i) => s2 + (parseFloat(i.subtotal) || 0), 0);

  /* ── Cobrança (fatura + duplicatas) ── */
  const setCobranca = (campo, valor) => setForm(f => ({ ...f, cobranca: { ...f.cobranca, [campo]: valor } }));
  const addDuplicata = () => setForm(f => ({
    ...f, cobranca: { ...f.cobranca, duplicatas: [...f.cobranca.duplicatas, { _key: keySeq++, numero: '', vencimento: '', valor: '' }] },
  }));
  const setDuplicata = (key, campo, valor) => setForm(f => ({
    ...f, cobranca: { ...f.cobranca, duplicatas: f.cobranca.duplicatas.map(d => d._key === key ? { ...d, [campo]: valor } : d) },
  }));
  const removerDuplicata = (key) => setForm(f => ({
    ...f, cobranca: { ...f.cobranca, duplicatas: f.cobranca.duplicatas.filter(d => d._key !== key) },
  }));

  /* ── Transporte (modalidade + transportadora + veículo + volumes) ── */
  const setTransporte = (campo, valor) => setForm(f => ({ ...f, transporte: { ...f.transporte, [campo]: valor } }));
  const addVolume = () => setForm(f => ({
    ...f, transporte: { ...f.transporte, volumes: [...f.transporte.volumes, { _key: keySeq++, qtd: '', especie: '', marca: '', numeracao: '', peso_liquido: '', peso_bruto: '' }] },
  }));
  const setVolume = (key, campo, valor) => setForm(f => ({
    ...f, transporte: { ...f.transporte, volumes: f.transporte.volumes.map(v => v._key === key ? { ...v, [campo]: valor } : v) },
  }));
  const removerVolume = (key) => setForm(f => ({
    ...f, transporte: { ...f.transporte, volumes: f.transporte.volumes.filter(v => v._key !== key) },
  }));

  /* ── Documentos Referenciados (<NFref>) ── */
  const addDocRef = () => setForm(f => ({
    ...f, documentos_referenciados: [...f.documentos_referenciados, { _key: keySeq++, tipo: 'nfe', chave_acesso: '' }],
  }));
  const setDocRef = (key, campo, valor) => setForm(f => ({
    ...f, documentos_referenciados: f.documentos_referenciados.map(d => d._key === key ? { ...d, [campo]: valor } : d),
  }));
  const removerDocRef = (key) => setForm(f => ({
    ...f, documentos_referenciados: f.documentos_referenciados.filter(d => d._key !== key),
  }));

  /* ── Pagamentos ── */
  const setPagamento = (key, campo, valor) => setForm(f => ({
    ...f, pagamentos: f.pagamentos.map(p => p._key === key
      ? { ...p, [campo]: valor, ...(campo === 'forma_pagamento' && valor === 'Sem Pagamento' ? { valor: '0' } : {}) }
      : p),
  }));
  const removerPagamento = (key) => setForm(f => ({ ...f, pagamentos: f.pagamentos.filter(p => p._key !== key) }));
  const addPagamento = () => setForm(f => ({
    ...f, pagamentos: [...f.pagamentos, { _key: keySeq++, forma_pagamento: 'Dinheiro', valor: '0' }],
  }));
  const totalPagamentos = form.pagamentos.reduce((s2, p) => s2 + (parseFloat(p.valor) || 0), 0);

  /* ── Emitir ── */
  // Corpo do POST /fiscal/nfe — reaproveitado tal e qual pelos previews
  // (totais no rodapé, DANFE "sem valor fiscal") via resolverParaEmissaoNfe
  // no backend: o que a tela MOSTRA é sempre o que ela EMITIRIA se o
  // usuário clicasse "Emitir" agora, nunca uma conta/PDF paralelos.
  const montarCorpoEmissao = (f) => ({
    venda_id: f.venda_id || undefined,
    documento_id: f.documento_id || undefined,
    tipo_movimento_fiscal_id: f.tipo_movimento_fiscal_id ? parseInt(f.tipo_movimento_fiscal_id) : undefined,
    cliente_id: f.cliente_id || undefined,
    itens: f.itens.map(i => ({
      produto_id: i.produto_id,
      descricao: i.descricao,
      unidade: i.unidade,
      quantidade: parseFloat(i.quantidade) || 0,
      preco_unitario: parseFloat(i.preco_unitario) || 0,
      subtotal: parseFloat(i.subtotal) || 0,
      cfop: i.cfop?.trim() || undefined,
      desconto_perc: i.desconto_perc !== '' ? i.desconto_perc : undefined,
      // NCM/CEST — editáveis direto na grade (aba Produtos). Achado real
      // (2026-09-16): nunca iam nesse corpo, então nota avulsa emitia sem
      // NCM (backend rejeita, "falta NCM") e "De uma Venda" descartava
      // qualquer edição de NCM/CEST silenciosamente (o merge no backend só
      // lia csosn/cst_icms/tributos, não esses dois — ver
      // fiscalController.resolverParaEmissaoNfe#itensOverride).
      ncm: i.ncm !== '' ? i.ncm : undefined,
      cest: i.cest !== '' ? i.cest : undefined,
      // Tributos editados no modal "Dados do Item" — undefined quando
      // vazio preserva o valor do cadastro (ver fiscalController.
      // emitirNfe#itensOverride, mescla com `??`).
      origem_fiscal: i.origem_fiscal !== '' ? i.origem_fiscal : undefined,
      csosn: i.csosn !== '' ? i.csosn : undefined,
      cst_icms: i.cst_icms !== '' ? i.cst_icms : undefined,
      icms_mod_bc: i.icms_mod_bc !== '' ? i.icms_mod_bc : undefined,
      icms_red_bc_perc: i.icms_red_bc_perc !== '' ? i.icms_red_bc_perc : undefined,
      icms_aliq_perc: i.icms_aliq_perc !== '' ? i.icms_aliq_perc : undefined,
      icms_dif_perc: i.icms_dif_perc !== '' ? i.icms_dif_perc : undefined,
      icms_st_mod_bc: i.icms_st_mod_bc !== '' ? i.icms_st_mod_bc : undefined,
      icms_st_red_bc_perc: i.icms_st_red_bc_perc !== '' ? i.icms_st_red_bc_perc : undefined,
      icms_st_mva_perc: i.icms_st_mva_perc !== '' ? i.icms_st_mva_perc : undefined,
      icms_st_aliq_perc: i.icms_st_aliq_perc !== '' ? i.icms_st_aliq_perc : undefined,
      icms_st_ret_bc: i.icms_st_ret_bc !== '' ? i.icms_st_ret_bc : undefined,
      icms_st_ret_valor: i.icms_st_ret_valor !== '' ? i.icms_st_ret_valor : undefined,
      pCredSN: i.pCredSN !== '' ? i.pCredSN : undefined,
      vCredICMSSN: i.vCredICMSSN !== '' ? i.vCredICMSSN : undefined,
    })),
    pagamentos: f.pagamentos.map(p => ({ forma_pagamento: p.forma_pagamento, valor: parseFloat(p.valor) || 0 })),
    // Aba "Dados da NF-e" — só os que o backend realmente transmite
    // (ver fiscalController.emitirNfe#camposManuais); os demais campos
    // da aba ficam só na tela por enquanto (situação do documento,
    // forma de pagamento de cabeçalho, aplicação material, IE ST,
    // CNPJ/usuário intermediador, receituário agrícola, CPF responsável
    // técnico — sem tag correspondente transmitida ainda).
    natureza_operacao: f.natureza_operacao?.trim() || undefined,
    tipo_doc: f.tipo_doc || undefined,
    finalidade_emissao: f.finalidade_emissao || undefined,
    indicador_presenca: f.indicador_presenca || undefined,
    indicador_finalizacao: f.indicador_finalizacao || undefined,
    destino_operacao: f.destino_operacao || undefined,
    indicador_intermediador: f.indicador_intermediador || undefined,
    endereco_entrega: f.endereco_entrega || undefined,
    saida_entrada: f.saida_entrada_marcado ? f.saida_entrada : undefined,
    // Abas Cobrança/Transporte/Documentos Referenciados/Informações
    // Compl. — ver fiscalController.emitirNfe#camposManuais.
    cobranca: (f.cobranca.numero_fatura || f.cobranca.valor_original || f.cobranca.duplicatas.length)
      ? { ...f.cobranca, duplicatas: f.cobranca.duplicatas.map(d => ({ numero: d.numero, vencimento: d.vencimento, valor: d.valor })) }
      : undefined,
    transporte: {
      ...f.transporte,
      veiculo_placa: f.transporte.veiculo_habilitado ? f.transporte.veiculo_placa : '',
      veiculo_uf: f.transporte.veiculo_habilitado ? f.transporte.veiculo_uf : '',
      veiculo_rntrc: f.transporte.veiculo_habilitado ? f.transporte.veiculo_rntrc : '',
      volumes: f.transporte.volumes.map(v => ({
        qtd: v.qtd, especie: v.especie, marca: v.marca, numeracao: v.numeracao,
        peso_liquido: v.peso_liquido, peso_bruto: v.peso_bruto,
      })),
    },
    documentos_referenciados: f.documentos_referenciados.map(d => ({ tipo: d.tipo, chave_acesso: d.chave_acesso })),
    informacoes_complementares: f.informacoes_complementares?.trim() || undefined,
  });

  const emitir = async () => {
    if ((!form.venda_id && !form.documento_id) || emitindo) return;
    if (!form.itens.length) { setErro('A nota precisa ter ao menos um item.'); setAba('produtos'); return; }
    if (form.origem === 'avulso' && !form.cliente_id) {
      setErro('Informe o destinatário antes de emitir.'); setAba('dados'); return;
    }
    setErro(''); setResultado(null); setEmitindo(true);
    try {
      const { data } = await api.post('/fiscal/nfe', montarCorpoEmissao(form));
      setResultado(data);
      if (data.sucesso) setAba('dados');
    } catch (err) {
      const d = err.response?.data || {};
      setErro(d.erro || 'Erro ao emitir a NF-e.');
      if (d.detalhes) setAba('produtos');
    } finally {
      setEmitindo(false);
    }
  };

  // Botão "DANFE" — sempre visível (pedido explícito), inclusive antes de
  // emitir. Documento já transmitido (autorizada/epec) usa o PDF oficial de
  // sempre; qualquer outro estado (rascunho, avulso, ainda editando) usa o
  // preview "sem valor fiscal / não transmitido", que reflete os dados como
  // estão na tela AGORA (mesmo corpo que "Emitir" mandaria).
  const abrirDanfe = async () => {
    if (abrindoDanfe) return;
    if (jaEmitido && form.documento_id) {
      setAbrindoDanfe(true);
      await abrirDanfeNfePdf(`/fiscal/nfe/${form.documento_id}/danfe`);
      setAbrindoDanfe(false);
      return;
    }
    if (!form.itens.length) { setErro('A nota precisa ter ao menos um item.'); setAba('produtos'); return; }
    if (!form.cliente_id) { setErro('Informe o destinatário antes de ver o DANFE.'); setAba('dados'); return; }
    setAbrindoDanfe(true);
    await abrirDanfeNfePdf('/fiscal/nfe/danfe-manual/preview', 'post', montarCorpoEmissao(form));
    setAbrindoDanfe(false);
  };

  const novoRascunho = () => {
    setForm(FORM_VAZIO); setResultado(null); setErro('');
    setModoEdicao(true); setResultadosBusca([]); setIndiceBusca(null);
    setAba('dados');
  };

  /* ── Salvar (manual) ── */
  const salvarAgora = async () => {
    if (!form.documento_id) return;
    setSalvando(true); setErro('');
    try {
      await api.post('/fiscal/nfe/rascunho', { id: form.documento_id, venda_id: form.venda_id || undefined, dados: form });
    } catch {
      setErro('Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  /* ── Excluir (só rascunho — nota já transmitida não sai daqui) ── */
  const excluirAtual = async () => {
    if (!form.documento_id || form.situacao !== 'rascunho') return;
    if (!window.confirm('Excluir este rascunho? Não pode ser desfeito.')) return;
    setExcluindo(true); setErro('');
    try {
      await api.delete(`/fiscal/nfe/rascunho/${form.documento_id}`);
      novoRascunho();
    } catch {
      setErro('Erro ao excluir.');
    } finally {
      setExcluindo(false);
    }
  };

  /* ── Buscar (rascunhos + já emitidas) ── */
  const buscarDocumentos = async (filtros) => {
    const { data } = await api.get('/fiscal/nfe/buscar', { params: filtros });
    setResultadosBusca(data);
    return data;
  };

  // Carrega um resultado da busca — rascunho abre pronto pra editar; já
  // transmitida abre só em modo leitura (pra reimprimir/cancelar, usar a
  // Central de Notas — este formulário não reconstrói produtos/tributos a
  // partir do XML já autorizado).
  const carregarRegistroBusca = async (doc, idx) => {
    setErro(''); setResultado(null);
    setIndiceBusca(idx);
    if (doc.status === 'rascunho') {
      setCarregandoVenda(true);
      try {
        const { data } = await api.get(`/fiscal/nfe/rascunho/${doc.id}`);
        setForm({ ...FORM_VAZIO, ...data.dados, documento_id: data.id, venda_id: data.venda_id || null, situacao: 'rascunho' });
        setModoEdicao(true);
      } catch {
        setErro('Erro ao carregar o rascunho.');
      } finally {
        setCarregandoVenda(false);
      }
    } else {
      setForm({
        ...FORM_VAZIO,
        documento_id: doc.id, venda_id: doc.venda_id || null,
        origem: doc.venda_id ? 'venda' : 'avulso',
        situacao: doc.status,
        venda_codigo: doc.venda_id ? sv(doc.venda_id) : '',
        cliente_nome: doc.cliente_nome || '',
        doc_fiscal_numero: doc.numero, doc_fiscal_serie: doc.serie,
      });
      setModoEdicao(false);
      setResultado({ sucesso: doc.status === 'autorizada', chave_acesso: doc.chave_acesso, motivo: `Situação: ${doc.status}` });
    }
    setAba('dados');
  };

  const abrirBuscar = async () => {
    setModalBuscar(true);
    await buscarDocumentos({}).catch(() => {});
  };

  // |< < > >| — navega pela última lista de resultados da busca, sem abrir
  // o modal de novo.
  const irParaResultado = (novoIdx) => {
    if (novoIdx < 0 || novoIdx >= resultadosBusca.length) return;
    carregarRegistroBusca(resultadosBusca[novoIdx], novoIdx);
  };

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🧾</span> Nota Fiscal — Emissão manual</div>

      {erro && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, margin: '8px 0', fontSize: 13 }}>
          ⚠ {erro}
        </div>
      )}
      {/* Venda já tem NF-e/NFC-e autorizada ou EPEC pendente — não deixa
          emitir outra por cima (backend também recusa, ver
          fiscalController.bloquearSeJaEmitido); precisa cancelar antes,
          na Central de Notas ou no F7 do PDV. */}
      {jaEmitido && (
        <div style={{ background: '#fef9c3', color: '#854d0e', padding: '8px 12px', borderRadius: 6, margin: '8px 0', fontSize: 13 }}>
          ⚠ Esta venda já tem uma {form.doc_fiscal_tipo === 'nfe' ? 'NF-e' : 'NFC-e'} nº {form.doc_fiscal_numero}
          {form.doc_fiscal_serie ? `/${form.doc_fiscal_serie}` : ''} {form.doc_fiscal_status === 'epec' ? 'em contingência (EPEC)' : 'autorizada'} —
          cancele-a antes (Central de Notas ou F7 do PDV) pra poder emitir outra.
        </div>
      )}
      {resultado && (
        <div style={{
          background: resultado.sucesso ? '#dcfce7' : '#fee2e2',
          color: resultado.sucesso ? '#166534' : '#991b1b',
          padding: '8px 12px', borderRadius: 6, margin: '8px 0', fontSize: 13,
        }}>
          {resultado.sucesso
            ? `NF-e autorizada! Chave: ${resultado.chave_acesso}`
            : `Rejeitada: ${resultado.motivo || 'ver detalhes'}`}
        </div>
      )}

      {/* ── Cabeçalho ── */}
      <div className={s.headerRow}>
        <div className={s.headerGroup}>
          <span className={s.headerLabel}>Situação</span>
          <input className={s.inputErp} style={{ width: 140 }} readOnly
            value={resultado ? (resultado.sucesso ? 'Autorizada' : 'Rejeitada') : (carregado ? 'Rascunho' : '—')} />
        </div>
        <div className={s.headerGroup}>
          <span className={s.headerLabel}>Origem</span>
          <input className={s.inputErp} style={{ width: 160 }} readOnly
            value={form.origem === 'avulso' ? 'Avulso' : carregado ? `Venda #${form.venda_codigo}` : '—'} />
        </div>
        {resultado?.chave_acesso && (
          <div className={s.headerGroup} style={{ flex: 1 }}>
            <span className={s.headerLabel}>Chave de Acesso</span>
            <input className={s.inputErp} style={{ flex: 1 }} readOnly value={resultado.chave_acesso} />
          </div>
        )}
      </div>

      {/* ── Abas ── */}
      <div className={s.tabBar}>
        {ABAS.map(a => (
          <button key={a.id} type="button"
            className={`${s.tab} ${aba === a.id ? s.tabAtiva : ''}`}
            onClick={() => setAba(a.id)}>
            {a.label}{!ABAS_PRONTAS.has(a.id) && <span style={{ marginLeft: 4, fontSize: 10, color: '#999' }}>(em breve)</span>}
          </button>
        ))}
      </div>

      <div className={s.tabContent}>
        {/* "Alterar" — registro carregado via Buscar (não-rascunho) entra
            travado; fieldset desabilita TODOS os campos/botões de edição
            de uma vez (menu de tabs fica FORA, continua navegável). */}
        <fieldset disabled={!modoEdicao} style={{ border: 0, margin: 0, padding: 0 }}>
        {!carregado && !carregandoVenda && (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            Clique em <b>Novo (F4)</b> para escolher a origem de uma nova NF-e.
          </div>
        )}
        {carregandoVenda && (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando venda...</div>
        )}

        {carregado && aba === 'dados' && (
          <div style={{ maxWidth: 980 }}>
            <div className={s.headerGroup} style={{ marginBottom: 10 }}>
              <span className={s.headerLabel}>Destinatário — Nome</span>
              <input className={s.inputErp} style={{ width: 320 }} readOnly value={form.cliente_nome} />
              <input className={s.inputErp} style={{ width: 160 }} readOnly value={form.cliente_cpf_cnpj || '—'} />
              <button className={s.btnLookup} type="button" title="Buscar/trocar cliente" onClick={() => setModalCliente(true)}>…</button>
            </div>

            <div className={s.headerGroup} style={{ marginBottom: 10 }}>
              <span className={s.headerLabel}>Endereço de entrega</span>
              {form.endereco_entrega ? (
                <>
                  <input className={s.inputErp} style={{ flex: 1, minWidth: 260 }} readOnly
                    value={`${form.endereco_entrega.logradouro}, ${form.endereco_entrega.numero} — ${form.endereco_entrega.bairro}, ${form.endereco_entrega.cidade}/${form.endereco_entrega.uf}`} />
                  <button className={s.btnErp} type="button" onClick={() => setModalEntrega(true)}>Informar</button>
                  <button className={s.btnErp} type="button" onClick={() => set('endereco_entrega', null)}>Limpar</button>
                </>
              ) : (
                <>
                  <input className={s.inputErp} style={{ flex: 1, minWidth: 260 }} readOnly
                    title="Endereço do cadastro do destinatário — clique em Informar pra usar um diferente"
                    value={clienteEndereco?.logradouro
                      ? `${clienteEndereco.logradouro}, ${clienteEndereco.numero || 'S/N'} — ${clienteEndereco.bairro || ''}, ${clienteEndereco.cidade || ''}/${clienteEndereco.estado || ''} (cadastro do destinatário)`
                      : (form.cliente_id ? 'Destinatário sem endereço cadastrado' : '— selecione o destinatário —')} />
                  <button className={s.btnErp} type="button" onClick={() => setModalEntrega(true)}>Informar</button>
                  <button className={s.btnErp} type="button" disabled>Limpar</button>
                </>
              )}
            </div>

            <div className={s.headerGroup} style={{ marginBottom: 14 }}>
              <span className={s.headerLabel}>Tipo de Movimento</span>
              <select className={s.selectErp} style={{ width: 340 }}
                value={form.tipo_movimento_fiscal_id}
                onChange={e => escolherTipoMovimento(e.target.value)}>
                <option value="">— usar regra automática da venda —</option>
                {tiposMovimento.map(t => (
                  <option key={t.id} value={t.id}>{t.nome} — CFOP {t.cfop_dentro_estado_id}/{t.cfop_fora_estado_id}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px 14px' }}>
              <div style={campoWrap}>
                <span style={campoLbl}>Emissão</span>
                <input className={s.inputErp} style={{ width: '100%', boxSizing: 'border-box' }} readOnly
                  title="Definida no momento da transmissão (não é editável antes) — aqui é só uma referência de agora"
                  value={new Date().toLocaleString('pt-BR')} />
              </div>

              <div style={campoWrap}>
                <span style={campoLbl}>
                  <input type="checkbox" checked={form.saida_entrada_marcado}
                    onChange={e => set('saida_entrada_marcado', e.target.checked)} /> Saída/Entrada
                </span>
                <input type="datetime-local" className={s.inputErp} style={{ width: '100%', boxSizing: 'border-box' }}
                  disabled={!form.saida_entrada_marcado} value={form.saida_entrada}
                  onChange={e => set('saida_entrada', e.target.value)} />
              </div>

              <CampoTexto label="Natureza operação" value={form.natureza_operacao}
                onChange={v => set('natureza_operacao', v)} placeholder="ex.: VENDA DE MERCADORIA" />

              <CampoSelect label="Destino da operação" value={form.destino_operacao}
                onChange={v => set('destino_operacao', v)} opts={DESTINO_OP_OPTS}
                info="Deixado em automático, é calculado pela UF do destinatário na hora de emitir — o mesmo cálculo de sempre. Só force um valor aqui se precisar de verdade." />

              <CampoSelect label="Modelo" value="55" onChange={() => {}} disabled
                opts={[{ value: '55', label: 'NF-e, modelo 55' }]}
                info="Emissão de NFC-e por esta tela ainda não disponível — continue usando o PDV/F7 pra NFC-e." />

              <CampoSelect label="Tipo doc." value={form.tipo_doc}
                onChange={v => set('tipo_doc', v)} opts={TIPO_DOC_OPTS} />

              <CampoSelect label="Finalidade emissão" value={form.finalidade_emissao}
                onChange={v => set('finalidade_emissao', v)} opts={FINALIDADE_OPTS} />

              <CampoSelect label="Situação do documento" value={form.situacao_documento}
                onChange={v => set('situacao_documento', v)} opts={SITUACAO_DOC_OPTS}
                info="Não é transmitido — o status real do documento (autorizada/rejeitada/cancelada) é definido pela SEFAZ, não escolhido aqui." />

              <CampoSelect label="Indicador presença" value={form.indicador_presenca}
                onChange={v => set('indicador_presenca', v)} opts={INDICADOR_PRESENCA_OPTS} />

              <CampoSelect label="Indicador finalização" value={form.indicador_finalizacao}
                onChange={v => set('indicador_finalizacao', v)} opts={INDICADOR_FINALIZACAO_OPTS} />

              <CampoSelect label="Forma pagamento" value={form.forma_pagamento_cab}
                onChange={v => set('forma_pagamento_cab', v)} opts={FORMA_PGT_HEADER_OPTS}
                info="Não é transmitido — o detalhamento real por forma de pagamento é a aba Formas Pagamento." />

              <CampoSelect label="Aplicação material" value={form.aplicacao_material}
                onChange={v => set('aplicacao_material', v)} opts={APLICACAO_MATERIAL_OPTS.map(x => ({ value: x, label: x }))}
                info="Não é transmitido — sem tag equivalente no leiaute da NF-e." />

              <CampoTexto label="IE ST" value={form.ie_st} onChange={v => set('ie_st', v)}
                info="Não é transmitido ainda." />

              <CampoSelect label="Indicador intermediador" value={form.indicador_intermediador}
                onChange={v => set('indicador_intermediador', v)} opts={INDICADOR_INTERMEDIADOR_OPTS} />

              <CampoTexto label="CNPJ intermediador" value={form.cnpj_intermediador}
                onChange={v => set('cnpj_intermediador', v)} disabled={form.indicador_intermediador !== '1'}
                info="Cadastrado, mas ainda não transmitido — a posição exata do grupo no XSD oficial precisa ser validada antes de habilitar." />

              <CampoTexto label="Usuário intermediador" value={form.usuario_intermediador}
                onChange={v => set('usuario_intermediador', v)} disabled={form.indicador_intermediador !== '1'}
                info="Cadastrado, mas ainda não transmitido (mesmo motivo do CNPJ intermediador)." />

              <CampoTexto label="Nº receituário agrícola" value={form.receituario_agricola}
                onChange={v => set('receituario_agricola', v)} info="Não é transmitido ainda." />

              <CampoTexto label="CPF responsável técnico" value={form.cpf_responsavel_tecnico}
                onChange={v => set('cpf_responsavel_tecnico', v)} info="Não é transmitido ainda." />
            </div>

            <p style={{ fontSize: 11, color: '#888', marginTop: 14 }}>
              O CFOP de cada item é resolvido automaticamente pelo Tipo de Movimento escolhido —
              pode ser sobrescrito item a item na aba Produtos. Campos marcados com <b>ⓘ</b> (passe o mouse
              pra ver o motivo) ainda não afetam a nota transmitida — servem só de cadastro por enquanto.
            </p>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10 }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && aba === 'produtos' && (
          <div>
            {form.origem === 'avulso' && (
              <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginBottom: 8 }}
                onClick={abrirF3} title="F3 — buscar produto pra adicionar (mesma busca do PDV)">
                + Adicionar Produto (F3)
              </button>
            )}
            <table className={s.itensGrid}>
              <thead>
                <tr>
                  <th className={s.tdSeq}>#</th>
                  <th style={{ width: 90 }}>Código</th>
                  <th className={s.tdDesc}>Descrição</th>
                  <th className={s.tdUn}>Un</th>
                  <th style={{ width: 90 }}>NCM</th>
                  <th style={{ width: 70 }}>{regimeNormal ? 'CST' : 'CSOSN'}</th>
                  <th className={s.tdQtd}>Quantidade</th>
                  <th className={s.tdPreco}>Valor Unit.</th>
                  <th style={{ width: 90 }}>CFOP</th>
                  <th style={{ width: 70 }}>(-) %</th>
                  <th className={s.tdSub}>Total</th>
                  <th style={{ width: 30 }}></th>
                  <th className={s.tdDel}></th>
                </tr>
              </thead>
              <tbody>
                {form.itens.map((it, idx) => (
                  <tr key={it._key}>
                    <td className={s.tdSeq}>{idx + 1}</td>
                    <td style={{ fontSize: 12, color: '#666' }}>{it.codigo_interno || '—'}</td>
                    <td className={s.tdDesc}>
                      <input className={s.cellInput} value={it.descricao}
                        onChange={e => setItem(it._key, 'descricao', e.target.value)} />
                    </td>
                    <td className={s.tdUn}>
                      <input className={s.cellInput} value={it.unidade}
                        onChange={e => setItem(it._key, 'unidade', e.target.value)} />
                    </td>
                    <td>
                      <input className={s.cellInput} style={{ width: 80 }} maxLength={8}
                        value={it.ncm} placeholder="NCM"
                        onChange={e => setItem(it._key, 'ncm', e.target.value.replace(/\D/g, ''))} />
                    </td>
                    <td>
                      <select className={s.cellInput} style={{ width: 64, padding: '0 2px' }}
                        value={(regimeNormal ? it.cst_icms : it.csosn) || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setItem(it._key, 'cst_icms', v);
                          setItem(it._key, 'csosn', v);
                        }}>
                        <option value="">—</option>
                        {(regimeNormal ? CST_ICMS_OPTS : CSOSN_OPTS).map(o => (
                          <option key={o.value} value={o.value}>{o.value}</option>
                        ))}
                      </select>
                    </td>
                    <td className={s.tdQtd}>
                      <input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.001"
                        ref={el => { focoQtdRef.current[it._key] = el; }}
                        value={it.quantidade}
                        onChange={e => setItem(it._key, 'quantidade', e.target.value)}
                        onKeyDown={e => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          focoPrecoRef.current[it._key]?.select();
                        }} />
                    </td>
                    <td className={s.tdPreco}>
                      <input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.01"
                        ref={el => { focoPrecoRef.current[it._key] = el; }}
                        value={it.preco_unitario}
                        onChange={e => setItem(it._key, 'preco_unitario', e.target.value)}
                        onKeyDown={e => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          e.target.blur();
                          // Lançamento guiado (só faz sentido lançando item a
                          // item — nota avulsa): Enter aqui já reabre a busca
                          // pro próximo produto, igual Pedidos/Orçamentos.
                          if (form.origem === 'avulso') abrirF3();
                        }} />
                    </td>
                    <td>
                      <input className={`${s.cellInput} ${s.cellInputNum}`} style={{ width: 70 }}
                        // Placeholder mostra o CFOP cadastrado no produto (em
                        // vez do texto genérico "auto") — só um hint visual,
                        // nunca é o valor de verdade do campo (`value`
                        // continua vazio até o usuário digitar). O CFOP que
                        // realmente vai pro XML quando este campo fica vazio
                        // é resolvido pelo Tipo de Movimento (ver aviso
                        // acima da grade) — pode divergir do placeholder em
                        // venda interestadual/ST/produção própria.
                        placeholder={it.cfop_saida || 'auto'}
                        title={it.cfop ? undefined : (it.cfop_saida ? `CFOP do cadastro do produto: ${it.cfop_saida}. Pode ser ajustado pelo Tipo de Movimento (venda interestadual/ST/produção própria).` : 'CFOP resolvido automaticamente pelo Tipo de Movimento.')}
                        value={it.cfop}
                        onChange={e => setItem(it._key, 'cfop', e.target.value)} />
                    </td>
                    <td>
                      <input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.01" min="0" max="100"
                        placeholder="0" style={{ width: 60 }}
                        value={it.desconto_perc}
                        onChange={e => setItem(it._key, 'desconto_perc', e.target.value)} />
                    </td>
                    <td className={s.tdSub}>R$ {fmtBRL(it.subtotal)}</td>
                    <td>
                      <button className={s.btnErp} style={{ minWidth: 24, padding: '0 6px' }}
                        title="Tributos do item (CST/CSOSN, ICMS, ICMS-ST, crédito Simples Nacional)"
                        onClick={() => setModalItem(it._key)}>🧮</button>
                    </td>
                    <td className={s.tdDel}>
                      <button className={s.btnErp} style={{ minWidth: 24, padding: '0 6px' }}
                        onClick={() => removerItem(it._key)}>✕</button>
                    </td>
                  </tr>
                ))}
                {form.itens.length === 0 && (
                  <tr><td colSpan={13} style={{ textAlign: 'center', color: '#888', padding: 20, fontStyle: 'italic' }}>
                    Sem itens de mercadoria nesta venda.
                  </td></tr>
                )}
              </tbody>
            </table>
            <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 'bold' }}>
              Total dos produtos: R$ {fmtBRL(totalItens)}
            </div>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10 }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && aba === 'pagamento' && (
          <div style={{ maxWidth: 520 }}>
            <table className={s.itensGrid}>
              <thead>
                <tr><th>Forma de Pagamento</th><th style={{ width: 140 }}>Valor</th><th className={s.tdDel}></th></tr>
              </thead>
              <tbody>
                {form.pagamentos.map(p => (
                  <tr key={p._key}>
                    <td>
                      <select className={s.selectErp} style={{ width: '100%' }}
                        value={p.forma_pagamento}
                        onChange={e => setPagamento(p._key, 'forma_pagamento', e.target.value)}>
                        {FORMA_PGT_OPTS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </td>
                    <td>
                      <input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.01"
                        disabled={p.forma_pagamento === 'Sem Pagamento'}
                        value={p.valor}
                        onChange={e => setPagamento(p._key, 'valor', e.target.value)} />
                    </td>
                    <td className={s.tdDel}>
                      <button className={s.btnErp} style={{ minWidth: 24, padding: '0 6px' }}
                        onClick={() => removerPagamento(p._key)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className={s.btnErp} style={{ marginTop: 8 }} onClick={addPagamento}>+ Forma de pagamento</button>
            <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 'bold' }}>
              Total pago: R$ {fmtBRL(totalPagamentos)}
            </div>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10 }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && aba === 'cobranca' && (
          <div style={{ maxWidth: 720 }}>
            <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Fatura</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px 14px', marginBottom: 18 }}>
              <CampoTexto label="Número" value={form.cobranca.numero_fatura} onChange={v => setCobranca('numero_fatura', v)} />
              <CampoTexto label="Valor original R$" value={form.cobranca.valor_original} onChange={v => setCobranca('valor_original', v)} />
              <CampoTexto label="Desconto R$" value={form.cobranca.valor_desconto} onChange={v => setCobranca('valor_desconto', v)} />
              <CampoTexto label="Valor líquido R$" value={form.cobranca.valor_liquido} onChange={v => setCobranca('valor_liquido', v)} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Duplicatas</div>
            <table className={s.itensGrid}>
              <thead><tr><th>Número</th><th style={{ width: 150 }}>Vencimento</th><th style={{ width: 140 }}>Valor R$</th><th className={s.tdDel}></th></tr></thead>
              <tbody>
                {form.cobranca.duplicatas.map(d => (
                  <tr key={d._key}>
                    <td><input className={s.cellInput} value={d.numero} onChange={e => setDuplicata(d._key, 'numero', e.target.value)} /></td>
                    <td><input type="date" className={s.cellInput} value={d.vencimento} onChange={e => setDuplicata(d._key, 'vencimento', e.target.value)} /></td>
                    <td><input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.01" value={d.valor} onChange={e => setDuplicata(d._key, 'valor', e.target.value)} /></td>
                    <td className={s.tdDel}>
                      <button className={s.btnErp} style={{ minWidth: 24, padding: '0 6px' }} onClick={() => removerDuplicata(d._key)}>✕</button>
                    </td>
                  </tr>
                ))}
                {form.cobranca.duplicatas.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', padding: 14, fontStyle: 'italic' }}>
                    Nenhuma duplicata — a nota pode ir sem parcelamento.
                  </td></tr>
                )}
              </tbody>
            </table>
            <button className={s.btnErp} style={{ marginTop: 8 }} onClick={addDuplicata}>+ Duplicata</button>
            <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
              Preencha manualmente aqui, ou deixe tudo em branco pra a nota sair sem grupo de cobrança.
            </p>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10 }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && aba === 'transporte' && (
          <div style={{ maxWidth: 820 }}>
            <div style={{ maxWidth: 320, marginBottom: 16 }}>
              <CampoSelect label="Modalidade do frete" value={form.transporte.modalidade_frete}
                onChange={v => setTransporte('modalidade_frete', v)} opts={MODALIDADE_FRETE_OPTS} />
            </div>

            <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Transportadora</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 14px', marginBottom: 18 }}>
              <CampoTexto label="Nome" value={form.transporte.transportadora_nome} onChange={v => setTransporte('transportadora_nome', v)} />
              <CampoTexto label="CNPJ/CPF" value={form.transporte.transportadora_cpf_cnpj} onChange={v => setTransporte('transportadora_cpf_cnpj', v)} />
              <CampoTexto label="IE" value={form.transporte.transportadora_ie} onChange={v => setTransporte('transportadora_ie', v)} />
              <CampoTexto label="Endereço" value={form.transporte.transportadora_endereco} onChange={v => setTransporte('transportadora_endereco', v)} />
              <CampoTexto label="Município" value={form.transporte.transportadora_municipio} onChange={v => setTransporte('transportadora_municipio', v)} />
              <CampoTexto label="UF" value={form.transporte.transportadora_uf} onChange={v => setTransporte('transportadora_uf', v.toUpperCase())} maxLength={2} />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>
              <input type="checkbox" checked={form.transporte.veiculo_habilitado}
                onChange={e => setTransporte('veiculo_habilitado', e.target.checked)} /> Veículo
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px 14px', marginBottom: 18 }}>
              <CampoTexto label="Placa" value={form.transporte.veiculo_placa} disabled={!form.transporte.veiculo_habilitado}
                onChange={v => setTransporte('veiculo_placa', v.toUpperCase())} />
              <CampoTexto label="RNTRC" value={form.transporte.veiculo_rntrc} disabled={!form.transporte.veiculo_habilitado}
                onChange={v => setTransporte('veiculo_rntrc', v)} />
              <CampoTexto label="UF" value={form.transporte.veiculo_uf} disabled={!form.transporte.veiculo_habilitado}
                onChange={v => setTransporte('veiculo_uf', v.toUpperCase())} maxLength={2} />
            </div>

            <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Volumes</div>
            <table className={s.itensGrid}>
              <thead><tr>
                <th style={{ width: 70 }}>Qtd.</th><th>Espécie</th><th>Marca</th><th>Num.</th>
                <th style={{ width: 110 }}>P. Líq. (kg)</th><th style={{ width: 110 }}>P. Bruto (kg)</th><th className={s.tdDel}></th>
              </tr></thead>
              <tbody>
                {form.transporte.volumes.map(v => (
                  <tr key={v._key}>
                    <td><input className={`${s.cellInput} ${s.cellInputNum}`} type="number" value={v.qtd} onChange={e => setVolume(v._key, 'qtd', e.target.value)} /></td>
                    <td><input className={s.cellInput} value={v.especie} onChange={e => setVolume(v._key, 'especie', e.target.value)} /></td>
                    <td><input className={s.cellInput} value={v.marca} onChange={e => setVolume(v._key, 'marca', e.target.value)} /></td>
                    <td><input className={s.cellInput} value={v.numeracao} onChange={e => setVolume(v._key, 'numeracao', e.target.value)} /></td>
                    <td><input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.001" value={v.peso_liquido} onChange={e => setVolume(v._key, 'peso_liquido', e.target.value)} /></td>
                    <td><input className={`${s.cellInput} ${s.cellInputNum}`} type="number" step="0.001" value={v.peso_bruto} onChange={e => setVolume(v._key, 'peso_bruto', e.target.value)} /></td>
                    <td className={s.tdDel}>
                      <button className={s.btnErp} style={{ minWidth: 24, padding: '0 6px' }} onClick={() => removerVolume(v._key)}>✕</button>
                    </td>
                  </tr>
                ))}
                {form.transporte.volumes.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 14, fontStyle: 'italic' }}>
                    Nenhum volume informado.
                  </td></tr>
                )}
              </tbody>
            </table>
            <button className={s.btnErp} style={{ marginTop: 8 }} onClick={addVolume}>+ Volume</button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10, display: 'block' }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && aba === 'docref' && (
          <div style={{ maxWidth: 780 }}>
            <table className={s.itensGrid}>
              <thead><tr><th style={{ width: 180 }}>Tipo</th><th>Chave de acesso / referência</th><th className={s.tdDel}></th></tr></thead>
              <tbody>
                {form.documentos_referenciados.map(d => (
                  <tr key={d._key}>
                    <td>
                      <select className={s.selectErp} style={{ width: '100%' }} value={d.tipo}
                        onChange={e => setDocRef(d._key, 'tipo', e.target.value)}>
                        {TIPO_DOC_REF_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <input className={s.cellInput} value={d.chave_acesso}
                        placeholder={TIPO_DOC_REF_WIREADO.has(d.tipo) ? 'Chave de acesso (44 dígitos)' : 'Referência (não transmitida ainda)'}
                        onChange={e => setDocRef(d._key, 'chave_acesso', e.target.value)} />
                      {!TIPO_DOC_REF_WIREADO.has(d.tipo) && (
                        <span title="Este tipo pede outro formato (UF/AAMM/CNPJ/modelo/série/número) — ainda não transmitido, só cadastro na tela por ora."
                          style={{ marginLeft: 6, color: '#b45309', cursor: 'help' }}>ⓘ</span>
                      )}
                    </td>
                    <td className={s.tdDel}>
                      <button className={s.btnErp} style={{ minWidth: 24, padding: '0 6px' }} onClick={() => removerDocRef(d._key)}>✕</button>
                    </td>
                  </tr>
                ))}
                {form.documentos_referenciados.length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', padding: 14, fontStyle: 'italic' }}>
                    Nenhum documento referenciado.
                  </td></tr>
                )}
              </tbody>
            </table>
            <button className={s.btnErp} style={{ marginTop: 8 }} onClick={addDocRef}>+ Inserir</button>
            <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
              NF-e/NFC-e/CT-e referenciam por chave de acesso (44 dígitos) e já são transmitidos de verdade.
              Os demais tipos (ⓘ) pedem outro formato — ficam só cadastrados por ora.
            </p>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10 }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && aba === 'infoadic' && (
          <div style={{ maxWidth: 720 }}>
            <span style={campoLbl}>Informações complementares</span>
            <textarea className={s.inputErp} style={{ width: '100%', minHeight: 140, boxSizing: 'border-box', marginTop: 4, resize: 'vertical' }}
              value={form.informacoes_complementares}
              onChange={e => set('informacoes_complementares', e.target.value)}
              placeholder="Texto livre — vai pra <infCpl> da nota." />
            <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
              "Valor Aproximado dos Tributos" (Lei da Transparência) não é calculado automaticamente aqui —
              precisaria de uma tabela de alíquotas IBPT que o sistema não tem hoje. Se precisar declarar o
              valor aproximado dos tributos, inclua o texto manualmente no campo acima.
            </p>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginTop: 10 }} onClick={proximaAba}>
              PRÓXIMO...
            </button>
          </div>
        )}

        {carregado && !ABAS_PRONTAS.has(aba) && (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            Aba <b>{ABAS.find(a => a.id === aba)?.label}</b> ainda não disponível nesta primeira versão.
          </div>
        )}
        </fieldset>
        {!modoEdicao && carregado && (
          <div style={{ background: '#fef9c3', color: '#854d0e', padding: '8px 12px', borderRadius: 6, margin: '10px 0', fontSize: 13 }}>
            🔒 Modo leitura — nota {form.situacao !== 'rascunho' ? `já ${form.situacao}` : 'carregada pra consulta'}.
            {form.situacao !== 'rascunho'
              ? ' Esta tela não reconstrói produtos/tributos de uma nota já transmitida — use a Central de Notas pra reimprimir DANFE, baixar XML ou cancelar.'
              : ' Clique em "Alterar" pra editar.'}
          </div>
        )}
      </div>

      {/* ── Rodapé ── */}
      <div className={s.statusBar} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {totais && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', columnGap: 18, rowGap: 2,
            paddingBottom: 6, marginBottom: 2, borderBottom: '1px solid var(--border, #e5e7eb)',
          }}>
            <span>Produtos: R$ {fmtBRL(totais.vProd)}</span>
            <span>Desconto: R$ {fmtBRL(totais.vDesc)}</span>
            <span>BC ICMS: R$ {fmtBRL(totais.vBC)}</span>
            <span>ICMS: R$ {fmtBRL(totais.vICMS)}</span>
            <span>BC ICMS-ST: R$ {fmtBRL(totais.vBCST)}</span>
            <span>ICMS-ST: R$ {fmtBRL(totais.vST)}</span>
            <span>PIS: R$ {fmtBRL(totais.vPIS)}</span>
            <span>COFINS: R$ {fmtBRL(totais.vCOFINS)}</span>
            <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--text)' }}>
              Total da NF-e: R$ {fmtBRL(totais.vNF)}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {form.origem === 'avulso' ? `Rascunho avulso — ${form.itens.length} item(ns)` :
            carregado ? `Venda #${form.venda_codigo} — ${form.itens.length} item(ns)` : 'Nenhuma nota carregada'}
          {resultadosBusca.length > 0 && indiceBusca != null && (
            <span style={{ marginLeft: 10, color: '#888' }}>— registro {indiceBusca + 1} de {resultadosBusca.length}</span>
          )}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {resultadosBusca.length > 0 && (
            <div style={{ display: 'flex', gap: 2, marginRight: 4 }}>
              <button className={s.btnErp} title="Primeiro registro" disabled={indiceBusca === 0}
                onClick={() => irParaResultado(0)}>|＜</button>
              <button className={s.btnErp} title="Registro anterior" disabled={!indiceBusca}
                onClick={() => irParaResultado((indiceBusca || 0) - 1)}>＜</button>
              <button className={s.btnErp} title="Próximo registro" disabled={indiceBusca == null || indiceBusca >= resultadosBusca.length - 1}
                onClick={() => irParaResultado((indiceBusca ?? -1) + 1)}>＞</button>
              <button className={s.btnErp} title="Último registro" disabled={indiceBusca == null || indiceBusca >= resultadosBusca.length - 1}
                onClick={() => irParaResultado(resultadosBusca.length - 1)}>＞|</button>
            </div>
          )}
          <button className={s.btnErp} onClick={abrirNovo}>Novo (F4)</button>
          <button className={s.btnErp} onClick={abrirBuscar}>Buscar</button>
          <button className={s.btnErp} onClick={salvarAgora} disabled={!form.documento_id || !modoEdicao || salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
          <button className={s.btnErp} onClick={() => setModoEdicao(true)} disabled={!form.documento_id || modoEdicao}>
            Alterar
          </button>
          <button className={s.btnErp} onClick={excluirAtual}
            disabled={!form.documento_id || form.situacao !== 'rascunho' || excluindo}
            title={form.situacao && form.situacao !== 'rascunho' ? 'Só é possível excluir enquanto não foi transmitida.' : undefined}>
            {excluindo ? 'Excluindo...' : 'Excluir'}
          </button>
          <button className={s.btnErp} onClick={abrirDanfe}
            disabled={!carregado || abrindoDanfe}
            title={jaEmitido ? 'DANFE oficial da nota transmitida.' : 'Prévia — nota ainda não transmitida, sem valor fiscal.'}>
            {abrindoDanfe ? 'Gerando...' : 'DANFE'}
          </button>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={emitir}
            disabled={(!form.venda_id && !form.documento_id) || !modoEdicao || emitindo || jaEmitido || (resultado && resultado.sucesso)}
            title={jaEmitido ? 'Venda já tem documento ativo — cancele-o antes de emitir outro.' : undefined}>
            {emitindo ? 'Emitindo...' : 'Emitir NF-e'}
          </button>
          {carregado && <button className={s.btnErp} onClick={novoRascunho}>Sair</button>}
        </div>
        </div>
      </div>

      {modalNovo && (
        <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && setModalNovo(false)}>
          <div className={s.modal} style={{ maxWidth: 360 }}>
            <div className={s.modalTitle}>Nova NF-e</div>
            <div className={s.modalBody} style={{ flexDirection: 'row', gap: 12, justifyContent: 'center', padding: 20 }}>
              <button className={s.btnErpPrimario} style={{ padding: '20px 24px', fontSize: 14 }} onClick={escolherOrigemVenda}>
                🛒<br />1. De uma Venda
              </button>
              <button className={s.btnErp} style={{ padding: '20px 24px', fontSize: 14 }} onClick={iniciarAvulso}
                title="Nota sem venda de origem — adicione os produtos e emita normalmente">
                📄<br />2. Avulso
              </button>
            </div>
            <div className={s.modalFooter}>
              <button className={s.btnErp} onClick={() => setModalNovo(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalSelecionarVenda && (
        <ModalSelecionarVenda onSelecionar={selecionarVenda} onClose={() => setModalSelecionarVenda(false)} />
      )}

      {modalCliente && (
        <ModalBuscaClienteSimples onSelect={selecionarCliente} onClose={() => setModalCliente(false)} />
      )}

      {modalEntrega && (
        <ModalEnderecoEntrega inicial={form.endereco_entrega}
          onSalvar={end => { set('endereco_entrega', end); setModalEntrega(false); }}
          onClose={() => setModalEntrega(false)} />
      )}

      {modalItem && (
        <ModalItemTributos item={form.itens.find(it => it._key === modalItem)} regimeNormal={regimeNormal}
          onSalvar={salvarTributosItem} onClose={() => setModalItem(null)} />
      )}

      <ModalBuscaProdutos modalF3={modalF3} fecharF3={fecharF3} filtrosF3={filtrosF3}
        setFiltro={setFiltro} setFiltrosF3={setFiltrosF3} FILTROS_VAZIOS={FILTROS_F3_VAZIOS}
        produtosF3={produtosF3} setProdutosF3={setProdutosF3}
        buscaRapidaF3={buscaRapidaF3} setBuscaRapidaF3={setBuscaRapidaF3}
        handleBuscaRapidaF3={handleBuscaRapidaF3} buscandoF3={buscandoF3}
        buscarF3={buscarF3} idxF3={idxF3} setIdxF3={setIdxF3}
        confirmarF3={confirmarF3} buscaRapidaRef={buscaRapidaRef} />

      {modalBuscar && (
        <ModalBuscarNfe onBuscar={buscarDocumentos} resultados={resultadosBusca}
          onSelecionar={(doc, idx) => { setModalBuscar(false); carregarRegistroBusca(doc, idx); }}
          onClose={() => setModalBuscar(false)} />
      )}
    </div>
  );
}
